import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { canUserDelete, canUserEdit } from '@/utils/permissions';
import {
  Plus, Search, RefreshCw, Truck, Trash2, X, Loader2, Package,
  Eye, Edit2,
  Save, ChevronDown, ChevronUp, FileText, Camera, Upload, AlertCircle, Briefcase,
} from 'lucide-react';
import { toast } from 'sonner';
import AttachmentGrid from '@/components/AttachmentGrid';

const STATUS_COLORS = {
  'Pending QA': 'bg-amber-100 text-amber-800',
  'Partial QA': 'bg-blue-100 text-blue-800',
  'QA Complete': 'bg-emerald-100 text-emerald-800',
  'Verified': 'bg-emerald-100 text-emerald-800',
  'Rejected': 'bg-rose-100 text-rose-800',
  'Processing': 'bg-gray-100 text-gray-800',
};

const WEIGHT_UOMS = ['kg', 'KG', 'KGS', 'kgs', 'MT', 'mt', 'g', 'gm', 'gram', 'tonne', 'TONNE'];

const blankItem = (id = 1) => ({
  id,
  commodityId: '',
  commodityName: '',
  hsnCode: '',
  uom: 'Nos',
  isWeightBased: false,
  // Weight-based
  grossWeight: '',
  tareWeight: '',
  packingWeight: '',
  netWeight: 0,
  // Quantity-based
  poQty: 0,
  invoiceQty: '',
  receivedQty: '',
  // Common
  rate: 0,
  gstRate: 18,
  amount: 0,
});

const emptyForm = () => ({
  grnNumber: '',
  poNumber: '',
  vendorName: '',
  vendorGst: '',
  projectId: '',
  projectName: '',
  poType: 'material',
  invoiceNo: '',
  invoiceDate: new Date().toISOString().split('T')[0],
  receivedDate: new Date().toISOString().split('T')[0],
  ewayBillNo: '',
  billTNo: '',
  vehicleNo: '',
  driverName: '',
  result: 'Accepted',
  remarks: '',
  // PO-derived metadata
  poUom: 'Nos',
  isWeightBased: false,
  poMiscCharges: [],
  items: [blankItem(1)],
});

export default function ProjectGRN({ poType: defaultPoType }) {
  const { API, user } = useContext(AppContext);
  const pageLabel = 'Project GRN';
  const canDelete = canUserDelete(user, '/projects/grn');
  const canEdit = canUserEdit(user, '/projects/grn');

  const [grns, setGrns] = useState([]);
  const [openPOs, setOpenPOs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [poTypeFilter, setPoTypeFilter] = useState(defaultPoType || 'material');
  const [expandedGRN, setExpandedGRN] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteImpact, setDeleteImpact] = useState(null);
  // Diagnose modal — click "🔍 Diagnose PO" in the create form to inspect
  // exactly why each PO item is pending/hidden. Uses the backend endpoint
  // `/projects/purchase-orders/{poNumber}/grn-diagnose`.
  const [diagnosePo, setDiagnosePo] = useState(null);
  const [diagnoseData, setDiagnoseData] = useState(null);
  const [diagnoseLoading, setDiagnoseLoading] = useState(false);
  // Full-details modal (mirrors Stores GRN eye-icon view)
  const [viewGRN, setViewGRN] = useState(null);
  // Edit-record modal
  const [editGRN, setEditGRN] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  const [form, setForm] = useState(emptyForm());
  // Master list of commodities available on the selected PO (for 1A+2A
  // constrained dropdown). Captured once on PO select; rows can only pick
  // from this list.
  const [poItems, setPoItems] = useState([]);
  // Vendor filter inside the New GRN modal → narrows the PO dropdown to
  // pending POs from the chosen vendor (mirrors the Stores GRN UX).
  const [vendorFilter, setVendorFilter] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Photo/document uploads
  const [capturedImages, setCapturedImages] = useState([]); // base64 data URLs
  const fileInputRef = useRef(null);

  const fetchGRNs = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/projects/grn?poType=${poTypeFilter}&limit=500`);
      setGrns(r.data?.data || []);
    } catch (e) {
      toast.error('Failed to load Project GRNs');
    } finally {
      setLoading(false);
    }
  }, [API, poTypeFilter]);

  // Fetch diagnose data whenever the user opens the modal for a PO
  useEffect(() => {
    if (!diagnosePo) { setDiagnoseData(null); return; }
    setDiagnoseLoading(true);
    axios.get(`${API}/projects/purchase-orders/${encodeURIComponent(diagnosePo)}/grn-diagnose`)
      .then(r => setDiagnoseData(r.data))
      .catch(err => setDiagnoseData({ error: err?.response?.data?.detail || err.message }))
      .finally(() => setDiagnoseLoading(false));
  }, [diagnosePo, API]);

  const fetchOpenPOs = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/projects/open-purchase-orders?poType=all`);
      setOpenPOs(r.data?.data || []);
    } catch (e) {
      console.error('Failed to load open POs', e);
    }
  }, [API]);

  useEffect(() => {
    fetchGRNs();
    fetchOpenPOs();
  }, [fetchGRNs, fetchOpenPOs]);

  // ---- GRN number preview (the backend generates the final id, but show a hint) ----
  const nextGrnNumberHint = (() => {
    const year = new Date().getFullYear();
    const maxId = grns
      .map(g => g.id || '')
      .filter(id => id.startsWith(`PRJ-GRN-${year}-`))
      .map(id => parseInt(id.split('-').pop(), 10))
      .filter(n => !Number.isNaN(n))
      .reduce((m, n) => Math.max(m, n), 0);
    return `PRJ-GRN-${year}-${String(maxId + 1).padStart(4, '0')} (auto)`;
  })();

  const handleSelectPO = (poNumber) => {
    const po = openPOs.find(p => p.poNumber === poNumber);
    if (!po) return;
    const firstUom = po.items?.[0]?.uom || po.items?.[0]?.unit || 'Nos';
    const isWeightBased = WEIGHT_UOMS.includes(firstUom);
    // Defensive number parser: treats null/undefined/0/negative/'' as fall-through
    const _num = (v) => {
      const n = parseFloat(v);
      return Number.isFinite(n) && n > 0 ? n : 0;
    };
    // Capture the full PO commodity list (master for 1A+2A dropdown)
    const masterPoItems = (po.items || []).map((it, idx) => {
      // Cascade through EVERY plausible qty field name so weird data
      // (legacy `qty`, missing `remainingQty`, backend-only `orderedQty`)
      // still renders a usable number instead of a silent empty cell.
      const poQty =
        _num(it.remainingQty) ||
        _num(it.orderedQty) ||
        _num(it.quantity) ||
        _num(it.qty) ||
        _num(it.orderQty) ||
        _num(it.balanceQty) ||
        0;
      return {
        id: `po-item-${idx}`,
        commodityName: it.commodityName || it.name || '',
        hsnCode: it.hsnCode || '',
        uom: it.uom || it.unit || firstUom,
        isWeightBased: WEIGHT_UOMS.includes(it.uom || it.unit || firstUom),
        poQty,
        deliveredQty: _num(it.deliveredQty),
        orderedRaw: _num(it.orderedQty) || _num(it.quantity) || 0,
        rate: it.rate || 0,
        gstRate: it.gstRate || 18,
      };
    });
    setPoItems(masterPoItems);
    setForm(prev => ({
      ...prev,
      poNumber: po.poNumber,
      vendorName: po.vendorName || '',
      vendorGst: po.vendorGst || '',
      projectId: po.projectId || '',
      projectName: po.projectName || '',
      poType: po.poType || 'material',
      poUom: firstUom,
      isWeightBased,
      poMiscCharges: po.miscCharges || [],
      items: masterPoItems.map((m, idx) => ({
        id: idx + 1,
        commodityId: m.id,
        commodityName: m.commodityName,
        hsnCode: m.hsnCode,
        uom: m.uom,
        isWeightBased: m.isWeightBased,
        grossWeight: '',
        tareWeight: '',
        packingWeight: '',
        netWeight: 0,
        poQty: m.poQty,
        invoiceQty: '',
        receivedQty: '',
        rate: m.rate,
        gstRate: m.gstRate,
        amount: 0,
      })),
    }));
  };

  const updateItem = (idx, field, value) => {
    setForm(prev => {
      const items = [...prev.items];
      const it = { ...items[idx], [field]: value };
      // Use FORM-level isWeightBased so all rows follow the same input mode
      // as the table header (prevents Binding Wire being weight-input while
      // the header labels say "PO Qty / Received Qty").
      const useWeight = prev.isWeightBased;
      // Auto-compute netWeight for weight-based items
      if (useWeight) {
        const g = parseFloat(it.grossWeight) || 0;
        const t = parseFloat(it.tareWeight) || 0;
        const p = parseFloat(it.packingWeight) || 0;
        it.netWeight = Math.max(0, +(g - t - p).toFixed(3));
      } else if (field === 'receivedQty') {
        // For quantity-based, netWeight tracks receivedQty for backend parity
        it.netWeight = parseFloat(value) || 0;
      }
      const qty = useWeight
        ? (parseFloat(it.netWeight) || 0)
        : (parseFloat(it.receivedQty) || 0);
      const rate = parseFloat(it.rate) || 0;
      it.amount = +(qty * rate).toFixed(2);
      items[idx] = it;
      return { ...prev, items };
    });
  };

  const addItemRow = () => setForm(prev => ({
    ...prev,
    items: [...prev.items, {
      ...blankItem(prev.items.length + 1),
      // Inherit PO-level UOM/weight defaults so the row renders the right
      // input columns even before a commodity is selected.
      uom: prev.poUom || 'Nos',
      isWeightBased: prev.isWeightBased,
    }],
  }));

  // ----- 1A+2A constraint helpers -----
  // Filter master PO items to those NOT already chosen by another row
  // (keeps the row's own selection visible).
  const getAvailableItemsFor = (rowIdx) => {
    if (!poItems || poItems.length === 0) return [];
    const usedIds = form.items
      .map((it, i) => (i === rowIdx ? null : it.commodityId))
      .filter(Boolean);
    return poItems.filter(p => !usedIds.includes(p.id));
  };

  // Boolean → all PO items have been added to the form
  const allPoItemsUsed = (
    poItems.length > 0 &&
    poItems.every(p => form.items.some(it => it.commodityId === p.id))
  );

  // Apply a PO commodity to a row (hydrates rate / uom / poQty / hsnCode)
  const selectCommodityForRow = (rowIdx, poItemId) => {
    const m = poItems.find(p => p.id === poItemId);
    if (!m) return;
    setForm(prev => {
      const items = [...prev.items];
      const existing = items[rowIdx] || {};
      items[rowIdx] = {
        ...existing,
        commodityId: m.id,
        commodityName: m.commodityName,
        hsnCode: m.hsnCode,
        uom: m.uom,
        isWeightBased: m.isWeightBased,
        // Only hydrate rate / poQty when this row hasn't been edited yet
        rate: existing.rate || m.rate || 0,
        gstRate: existing.gstRate || m.gstRate || 18,
        poQty: existing.poQty || m.poQty || 0,
      };
      return { ...prev, items };
    });
  };

  const removeItemRow = (idx) => setForm(prev => ({
    ...prev,
    items: prev.items.length > 1 ? prev.items.filter((_, i) => i !== idx) : prev.items,
  }));

  const grnTotals = form.items.reduce((acc, item) => ({
    totalNetWeight: acc.totalNetWeight + (parseFloat(item.netWeight) || 0),
    totalReceivedQty: acc.totalReceivedQty + (parseFloat(form.isWeightBased ? item.netWeight : item.receivedQty) || 0),
    totalAmount: acc.totalAmount + (parseFloat(item.amount) || 0),
  }), { totalNetWeight: 0, totalReceivedQty: 0, totalAmount: 0 });

  // ---- File upload ----
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => setCapturedImages(prev => [...prev, ev.target.result]);
      reader.readAsDataURL(file);
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const resetForm = () => {
    setForm(emptyForm());
    setPoItems([]);
    setVendorFilter('');
    setCapturedImages([]);
  };

  const submitGRN = async (e) => {
    e.preventDefault();
    if (!form.poNumber) {
      toast.error('Please select a Pending Project PO');
      return;
    }
    if (!form.invoiceNo) {
      toast.error('Invoice number is required');
      return;
    }
    const validItems = form.items.filter(it => {
      const qty = form.isWeightBased ? parseFloat(it.netWeight) : parseFloat(it.receivedQty);
      return qty > 0 && it.commodityName;
    });
    if (validItems.length === 0) {
      toast.error('Enter received quantity for at least one item');
      return;
    }
    // Final client-side Balance PO Qty guard — server has the same guard,
    // this just gives instant feedback instead of a round-trip 400.
    for (const it of validItems) {
      const balance = parseFloat(it.poQty) || 0;
      const recv = form.isWeightBased ? (parseFloat(it.netWeight) || 0) : (parseFloat(it.receivedQty) || 0);
      if (recv > balance + 0.001) {
        toast.error(`"${it.commodityName}" — Received Qty ${recv} exceeds Balance PO Qty ${balance} ${it.uom || ''}`);
        setSubmitting(false);
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        supplier: form.vendorName,
        items: validItems.map(it => ({
          commodityName: it.commodityName,
          hsnCode: it.hsnCode || '',
          uom: it.uom || 'Nos',
          poQty: parseFloat(it.poQty) || 0,
          // Weight fields
          grossWeight: parseFloat(it.grossWeight) || 0,
          tareWeight: parseFloat(it.tareWeight) || 0,
          packingWeight: parseFloat(it.packingWeight) || 0,
          netWeight: parseFloat(it.netWeight) || 0,
          // Qty fields
          invoiceQty: parseFloat(it.invoiceQty) || 0,
          receivedQty: form.isWeightBased
            ? (parseFloat(it.netWeight) || 0)
            : (parseFloat(it.receivedQty) || 0),
          quantity: form.isWeightBased
            ? (parseFloat(it.netWeight) || 0)
            : (parseFloat(it.receivedQty) || 0),
          rate: parseFloat(it.rate) || 0,
          gstRate: parseFloat(it.gstRate) || 18,
          amount: parseFloat(it.amount) || 0,
          isWeightBased: form.isWeightBased,
        })),
        // Carry top-level fields for compatibility
        images: capturedImages,
        hasImage: capturedImages.length > 0,
      };
      const r = await axios.post(`${API}/projects/grn`, payload);
      toast.success(`Project GRN ${r.data.id} created — sent to QA`);
      setShowForm(false);
      resetForm();
      await fetchGRNs();
      await fetchOpenPOs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create GRN');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = async (grn) => {
    setDeletingId(grn.id);
    try {
      const r = await axios.get(`${API}/projects/grn/${grn.id}/delete-impact`);
      setDeleteImpact({ grn, impact: r.data });
    } catch (e) {
      toast.error('Could not load delete impact');
      setDeletingId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteImpact) return;
    try {
      await axios.delete(`${API}/projects/grn/${deleteImpact.grn.id}`);
      toast.success('Project GRN deleted (cascade)');
      setDeleteImpact(null);
      setDeletingId(null);
      await fetchGRNs();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  // Edit GRN — opens a small modal with the editable fields and PUTs back
  const openEdit = (grn) => {
    setEditGRN(grn);
    setEditForm({
      status: grn.status || 'Pending QA',
      invoiceNo: grn.invoiceNo || '',
      ewayBillNo: grn.ewayBillNo || '',
      billTNo: grn.billTNo || '',
      receivedDate: grn.receivedDate || grn.date || '',
      remarks: grn.remarks || '',
    });
  };

  const submitEdit = async () => {
    if (!editGRN) return;
    setSavingEdit(true);
    try {
      await axios.put(`${API}/projects/grn/${editGRN.id}`, editForm);
      toast.success('GRN updated');
      setEditGRN(null);
      await fetchGRNs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update GRN');
    } finally {
      setSavingEdit(false);
    }
  };

  const filtered = grns.filter(g => {
    const matchesSearch = !searchTerm ||
      g.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      g.poNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      g.invoiceNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      g.vendorName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || g.status === statusFilter;
    const matchesTab = poTypeFilter === 'all'
      || (poTypeFilter === 'material' && (g.poType || 'material') === 'material')
      || (poTypeFilter === 'service' && g.poType === 'service');
    return matchesSearch && matchesStatus && matchesTab;
  });

  const vendorPOs = openPOs.filter(p => !vendorFilter || p.vendorName === vendorFilter);
  const distinctVendors = [...new Set(openPOs.map(p => p.vendorName).filter(Boolean))].sort();

  // Summary stats — mirror Stores GRN
  const tabbedGrns = grns.filter(g =>
    poTypeFilter === 'all'
    || (poTypeFilter === 'material' && (g.poType || 'material') === 'material')
    || (poTypeFilter === 'service' && g.poType === 'service'),
  );
  const totalGRN = tabbedGrns.length;
  const pendingGRN = tabbedGrns.filter(g => g.status === 'Pending' || g.status === 'Pending QA' || g.status === 'Partial QA').length;
  const verifiedGRN = tabbedGrns.filter(g => g.status === 'Verified' || g.status === 'QA Complete').length;
  const shortageGRN = tabbedGrns.filter(g => g.result === 'Shortage').length;

  const getResultColor = (result) => result === 'Shortage'
    ? 'bg-red-100 text-red-700 border-red-200 font-bold'
    : 'bg-green-100 text-green-700 border-green-200';

  return (
    <div className="space-y-4" data-testid="project-grn-page">
      {/* Header — matches Stores GRN style */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg">
            <FileText className="text-white" size={24} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Project GRN - Goods Receipt Note</h1>
            <p className="text-sm text-gray-500">Receive materials &amp; services against any open Project PO · isolated from Stores stock · flows to Project QA</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
            <Search size={16} className="text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search GRN, Invoice, Vendor..."
              className="bg-transparent border-none outline-none text-sm w-48"
              data-testid="project-grn-search-input"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-amber-400"
            data-testid="project-grn-status-filter"
          >
            <option value="all">All Status</option>
            <option value="Pending QA">Pending QA</option>
            <option value="Partial QA">Partial QA</option>
            <option value="QA Complete">QA Complete</option>
            <option value="Verified">Verified</option>
            <option value="Rejected">Rejected</option>
          </select>
          <button
            onClick={() => { fetchGRNs(); fetchOpenPOs(); }}
            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg"
            title="Refresh"
            data-testid="project-grn-refresh-btn"
          >
            <RefreshCw size={14} />
          </button>
          <button
            onClick={() => { setShowForm(true); resetForm(); }}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 shadow font-medium text-sm"
            data-testid="project-grn-new-btn"
          >
            <Plus size={18} /> New GRN
          </button>
        </div>
      </div>

      {/* Material GRN / Service GRN Tabs — mirrors Stores GRN */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex">
          <button
            onClick={() => setPoTypeFilter('material')}
            className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold transition border-b-2 ${
              poTypeFilter === 'material'
                ? 'border-amber-500 text-amber-700 bg-amber-50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
            data-testid="project-grn-material-tab"
          >
            <Package size={18} />
            Material GRN
          </button>
          <button
            onClick={() => setPoTypeFilter('service')}
            className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold transition border-b-2 ${
              poTypeFilter === 'service'
                ? 'border-indigo-500 text-indigo-700 bg-indigo-50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
            data-testid="project-grn-service-tab"
          >
            <Briefcase size={18} />
            Service GRN
          </button>
        </div>
      </div>

      {/* Summary Cards — mirrors Stores GRN */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white" data-testid="project-grn-stat-total">
          <p className="text-xs opacity-80">Total GRN</p>
          <p className="text-2xl font-bold">{totalGRN}</p>
        </div>
        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl p-4 text-white" data-testid="project-grn-stat-pending">
          <p className="text-xs opacity-80">Pending</p>
          <p className="text-2xl font-bold">{pendingGRN}</p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white" data-testid="project-grn-stat-verified">
          <p className="text-xs opacity-80">Verified</p>
          <p className="text-2xl font-bold">{verifiedGRN}</p>
        </div>
        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-4 text-white" data-testid="project-grn-stat-shortage">
          <div className="flex items-center gap-1">
            <AlertCircle size={14} />
            <p className="text-xs opacity-80">Shortage</p>
          </div>
          <p className="text-2xl font-bold">{shortageGRN}</p>
        </div>
      </div>

      {/* GRN Card List — mirrors Stores QA / Project QA card layout */}
      <div className="space-y-3">
        {loading ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400">
            <Loader2 className="animate-spin mx-auto mb-2" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-500">
            <Package className="mx-auto mb-2 text-gray-300" size={36} />
            No Project GRN records found.
          </div>
        ) : filtered.map(g => {
          const firstItem = (g.items || [])[0] || {};
          const totalQty = (g.items || []).reduce(
            (s, i) => s + (parseFloat(i.receivedQty) || parseFloat(i.netWeight) || parseFloat(i.quantity) || 0),
            0,
          );
          const expandedNow = expandedGRN === g.id;
          const commodityLabel = (g.items || []).length > 1
            ? `${firstItem.commodityName || ''} +${g.items.length - 1} more`
            : (firstItem.commodityName || g.material || '-');
          const hasImg = g.hasImage || (g.images && g.images.length > 0);

          return (
            <div key={g.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              {/* Card header */}
              <div
                className="flex items-center justify-between px-5 py-3.5 cursor-pointer hover:bg-amber-50/40 transition"
                onClick={() => setExpandedGRN(expandedNow ? null : g.id)}
                data-testid={`project-grn-row-${g.id}`}
              >
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${STATUS_COLORS[g.status] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                    {g.status}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm font-semibold text-amber-600">{g.id}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium uppercase ${
                        g.poType === 'service' ? 'bg-indigo-100 text-indigo-700' : 'bg-teal-100 text-teal-700'
                      }`}>
                        {g.poType === 'service' ? 'SVC' : 'MAT'}
                      </span>
                      <span className="text-gray-300">|</span>
                      <span className="text-sm font-medium text-gray-900 truncate">{commodityLabel}</span>
                      {(g.items?.length || 0) > 1 && (
                        <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-[10px] font-medium">
                          {g.items.length} items
                        </span>
                      )}
                      {g.result && g.result !== 'Accepted' && (
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${getResultColor(g.result)}`}>
                          {g.result === 'Shortage' && <AlertCircle size={10} className="inline mr-0.5" />}
                          {g.result}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500 flex-wrap">
                      <span>PO: {g.poNumber || '-'}</span>
                      <span>Inv: <span className="font-medium text-blue-600">{g.invoiceNo || '-'}</span></span>
                      <span>{g.vendorName || g.supplier || '-'}</span>
                      <span>{g.date || g.receivedDate || '-'}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider">Qty</p>
                    <p className="text-sm font-bold text-blue-600 tabular-nums">
                      {totalQty.toLocaleString('en-IN')}
                      <span className="text-[10px] text-gray-500 font-normal ml-1">{firstItem.uom || g.uom || '-'}</span>
                    </p>
                  </div>
                  {hasImg && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded-lg text-xs">
                      <FileText size={12} /> {(g.images || []).length || 1}
                    </span>
                  )}
                  <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => setViewGRN(g)}
                      className="p-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg"
                      title="View GRN Details"
                      data-testid={`project-grn-eye-${g.id}`}
                    >
                      <Eye size={16} />
                    </button>
                    {canEdit && (
                      <button
                        onClick={() => openEdit(g)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg text-xs font-medium hover:bg-amber-100 transition"
                        title="Edit GRN"
                        data-testid={`project-grn-edit-${g.id}`}
                      >
                        <Edit2 size={14} /> Edit
                      </button>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => handleDeleteClick(g)}
                        disabled={deletingId === g.id}
                        className="p-1.5 bg-rose-50 text-rose-600 hover:bg-rose-100 rounded-lg disabled:opacity-50"
                        title="Delete GRN (cascade)"
                        data-testid={`project-grn-delete-${g.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                    {expandedNow
                      ? <ChevronUp size={18} className="text-gray-400" />
                      : <ChevronDown size={18} className="text-gray-400" />}
                  </div>
                </div>
              </div>

              {/* Expanded details — items table + meta + attachments */}
              {expandedNow && (
                <div className="border-t border-gray-100 px-5 py-4 bg-gray-50/40 space-y-3">
                  <h4 className="text-xs font-semibold text-gray-700">
                    Items received · Total ₹{(g.totalAmount || 0).toLocaleString('en-IN')}
                  </h4>
                  <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-gray-500 border-b border-gray-200 bg-gray-50">
                          <th className="px-3 py-2">Commodity</th>
                          <th className="px-3 py-2 text-right">Gross (kg)</th>
                          <th className="px-3 py-2 text-right">Tare (kg)</th>
                          <th className="px-3 py-2 text-right">Packing (kg)</th>
                          <th className="px-3 py-2 text-right">Net / Qty</th>
                          <th className="px-3 py-2 text-right">UoM</th>
                          <th className="px-3 py-2 text-right">Rate</th>
                          <th className="px-3 py-2 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(g.items || []).map((it, idx) => (
                          <tr key={idx} className="border-b border-gray-100 last:border-b-0">
                            <td className="px-3 py-1.5">{it.commodityName}</td>
                            <td className="px-3 py-1.5 text-right">{it.grossWeight ? Number(it.grossWeight).toLocaleString('en-IN') : '-'}</td>
                            <td className="px-3 py-1.5 text-right">{it.tareWeight ? Number(it.tareWeight).toLocaleString('en-IN') : '-'}</td>
                            <td className="px-3 py-1.5 text-right">{it.packingWeight ? Number(it.packingWeight).toLocaleString('en-IN') : '-'}</td>
                            <td className="px-3 py-1.5 text-right font-medium">{(it.receivedQty || it.netWeight || it.quantity || 0).toLocaleString('en-IN')}</td>
                            <td className="px-3 py-1.5 text-right">{it.uom}</td>
                            <td className="px-3 py-1.5 text-right">₹{(it.rate || 0).toLocaleString('en-IN')}</td>
                            <td className="px-3 py-1.5 text-right font-medium">₹{(it.amount || 0).toLocaleString('en-IN')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex items-center gap-4 text-[11px] text-gray-600 flex-wrap">
                    {g.vehicleNo && <span><strong>Vehicle:</strong> {g.vehicleNo}</span>}
                    {g.driverName && <span><strong>Driver:</strong> {g.driverName}</span>}
                    {g.ewayBillNo && <span><strong>Eway Bill:</strong> {g.ewayBillNo}</span>}
                    {g.billTNo && <span><strong>Bill T:</strong> {g.billTNo}</span>}
                    {g.projectName && <span><strong>Project:</strong> {g.projectName}</span>}
                  </div>
                  {g.remarks && (
                    <p className="text-[11px] text-gray-600"><strong>Remarks:</strong> {g.remarks}</p>
                  )}
                  {(() => {
                    const attachments = g.images || g.documents || g.photos || [];
                    if (!Array.isArray(attachments) || attachments.length === 0) return null;
                    return (
                      <div className="pt-3 border-t border-gray-200">
                        <h4 className="text-xs font-semibold text-gray-700 mb-2">
                          Photos / Documents ({attachments.length})
                        </h4>
                        <AttachmentGrid attachments={attachments} columns={4} />
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* CREATE GRN MODAL — mirrors Stores GRN create card */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <form onSubmit={submitGRN} className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div className="flex items-center gap-2">
                <FileText className="text-amber-500" size={20} />
                <h2 className="font-bold text-lg">
                  Create New {form.poType === 'service' ? 'Service ' : ''}Project GRN
                </h2>
              </div>
              <button type="button" onClick={() => { setShowForm(false); resetForm(); }}
                className="p-1 hover:bg-gray-100 rounded">
                <X size={20} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* GRN & Invoice Details */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">GRN Number</label>
                  <input
                    type="text"
                    value={nextGrnNumberHint}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50 font-mono"
                    data-testid="project-grn-number-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Invoice Number <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={form.invoiceNo}
                    onChange={e => setForm(f => ({ ...f, invoiceNo: e.target.value }))}
                    placeholder="INV-XXX-2026-0001"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-amber-400 outline-none"
                    data-testid="project-grn-invoice-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Eway Bill No</label>
                  <input
                    type="text"
                    value={form.ewayBillNo}
                    onChange={e => setForm(f => ({ ...f, ewayBillNo: e.target.value }))}
                    placeholder="EWAY-XXXXX"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-amber-400 outline-none"
                    data-testid="project-grn-eway-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Bill T No</label>
                  <input
                    type="text"
                    value={form.billTNo}
                    onChange={e => setForm(f => ({ ...f, billTNo: e.target.value }))}
                    placeholder="BILLT-XXXXX"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-amber-400 outline-none"
                    data-testid="project-grn-billt-input"
                  />
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Invoice Date</label>
                  <input
                    type="date"
                    value={form.invoiceDate}
                    onChange={e => setForm(f => ({ ...f, invoiceDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-amber-400 outline-none"
                    data-testid="project-grn-invoice-date"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Received Date</label>
                  <input
                    type="date"
                    value={form.receivedDate}
                    onChange={e => setForm(f => ({ ...f, receivedDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-amber-400 outline-none"
                    data-testid="project-grn-received-date"
                  />
                </div>
              </div>

              {/* Vendor & PO Selection (mirrors Stores GRN) */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Package size={16} className="text-amber-500" />
                  Vendor &amp; Project Purchase Order
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Vendor <span className="text-red-500">*</span></label>
                    <select
                      value={vendorFilter}
                      onChange={e => {
                        const v = e.target.value;
                        setVendorFilter(v);
                        setForm(f => ({ ...emptyForm(), invoiceNo: f.invoiceNo, invoiceDate: f.invoiceDate, receivedDate: f.receivedDate, ewayBillNo: f.ewayBillNo, billTNo: f.billTNo, vendorName: v }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-amber-400 outline-none"
                      data-testid="project-grn-vendor-select"
                      required
                    >
                      <option value="">— Select vendor —</option>
                      {distinctVendors.map(vn => (
                        <option key={vn} value={vn}>{vn}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center justify-between">
                      <span>PO Number <span className="text-red-500">*</span></span>
                      {form.poNumber && (
                        <button
                          type="button"
                          onClick={() => setDiagnosePo(form.poNumber)}
                          className="text-[11px] font-normal text-amber-700 hover:text-amber-900 hover:underline"
                          data-testid="project-grn-diagnose-po-btn"
                          title="See exactly why each PO item is pending or hidden"
                        >
                          🔍 Diagnose PO
                        </button>
                      )}
                    </label>
                    <select
                      value={form.poNumber}
                      onChange={e => handleSelectPO(e.target.value)}
                      required
                      disabled={!vendorFilter}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-amber-400 outline-none disabled:bg-gray-50 disabled:text-gray-400"
                      data-testid="project-grn-po-select"
                    >
                      <option value="">
                        {vendorFilter ? '— Select open PO —' : 'Pick a vendor first…'}
                      </option>
                      {vendorPOs.map(p => (
                        <option key={p.poNumber} value={p.poNumber}>
                          [{(p.poType || 'material').toUpperCase()}] {p.poNumber} — {p.items.length} items pending
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Auto-filled info bar (mirrors Stores) */}
                {form.vendorName && (
                  <div className="mt-3 p-3 bg-amber-50 rounded-lg text-sm">
                    <div className="flex items-center gap-4 flex-wrap">
                      <div>
                        <span className="text-gray-500 text-xs">Vendor GST:</span>
                        <span className="ml-2 font-medium">{form.vendorGst || '-'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500 text-xs">Project:</span>
                        <span className="ml-2 font-medium">{form.projectName || '-'}</span>
                      </div>
                      {vendorPOs.length === 0 && (
                        <p className="text-xs text-green-600 flex items-center gap-1">
                          <span>✓</span> All POs for {form.vendorName} are fully delivered
                        </p>
                      )}
                      {form.poNumber && form.items.length > 0 && (
                        <p className="text-xs text-blue-600 flex items-center gap-1">
                          <span>📦</span> PO has {form.items.length} item(s)
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Multi-Item Commodity Entry (mirrors Stores GRN exactly) */}
              {form.poNumber && (
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <Package size={16} className="text-purple-500" />
                      {form.poType === 'service' ? 'Service' : 'Commodity'} Items ({form.items.length})
                      <span className="text-xs text-gray-500 font-normal ml-2">
                        (UOM: {form.poUom}{form.isWeightBased ? ' · weight-based' : ' · qty-based'})
                      </span>
                    </h3>
                    <button
                      type="button"
                      onClick={addItemRow}
                      disabled={allPoItemsUsed}
                      title={allPoItemsUsed ? 'All PO items already added' : 'Add another commodity row'}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg transition text-sm font-medium ${
                        allPoItemsUsed
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                      }`}
                      data-testid="project-grn-add-item-btn"
                    >
                      <Plus size={14} />
                      Add Item
                    </button>
                  </div>
                  {allPoItemsUsed && (
                    <div className="text-[11px] text-gray-500 mb-2 italic">
                      All commodities from this PO have been added. Off-PO items are not allowed.
                    </div>
                  )}

                  <div className="bg-gray-50 rounded-lg overflow-hidden border border-gray-200">
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-2 py-2 text-left font-semibold text-gray-700 w-8">#</th>
                            <th className="px-2 py-2 text-left font-semibold text-gray-700 min-w-[200px]">
                              {form.poType === 'service' ? 'Service (SAC)' : 'Commodity (HSN)'}
                            </th>
                            {form.isWeightBased ? (
                              <>
                                <th className="px-2 py-2 text-right font-semibold text-gray-700 w-24">Gross (kg)</th>
                                <th className="px-2 py-2 text-right font-semibold text-gray-700 w-24">Tare (kg)</th>
                                <th className="px-2 py-2 text-right font-semibold text-gray-700 w-24">Packing (kg)</th>
                                <th className="px-2 py-2 text-right font-semibold text-gray-700 w-24">Net (kg)</th>
                              </>
                            ) : (
                              <>
                                <th className="px-2 py-2 text-right font-semibold text-gray-700 w-28" title="Balance = PO Qty − sum of previous GRNs (rejected qty is released back)">Balance PO Qty</th>
                                <th className="px-2 py-2 text-right font-semibold text-gray-700 w-24">Invoice Qty</th>
                                <th className="px-2 py-2 text-right font-semibold text-gray-700 w-24">Received Qty</th>
                                <th className="px-2 py-2 text-center font-semibold text-gray-700 w-24">Status</th>
                              </>
                            )}
                            <th className="px-2 py-2 text-right font-semibold text-gray-700 w-24">Rate (₹)</th>
                            <th className="px-2 py-2 text-right font-semibold text-gray-700 w-28">Amount (₹)</th>
                            <th className="px-2 py-2 text-center font-semibold text-gray-700 w-12"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {form.items.map((item, idx) => (
                            <tr key={item.id} className="bg-white">
                              <td className="px-2 py-2 text-gray-500 font-medium">{idx + 1}</td>
                              <td className="px-2 py-2">
                                {(() => {
                                  const availableItems = getAvailableItemsFor(idx);
                                  return (
                                    <select
                                      value={item.commodityId || ''}
                                      onChange={e => selectCommodityForRow(idx, e.target.value)}
                                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:border-amber-400 outline-none bg-white"
                                      data-testid={`project-grn-commodity-${idx}`}
                                      required
                                    >
                                      <option value="">Select commodity from PO...</option>
                                      {availableItems.map(po => (
                                        <option key={po.id} value={po.id}>
                                          {po.commodityName}
                                        </option>
                                      ))}
                                    </select>
                                  );
                                })()}
                                {item.hsnCode && (
                                  <div className="text-[10px] text-gray-400 mt-1 font-mono">
                                    {form.poType === 'service' ? 'SAC' : 'HSN'}: {item.hsnCode}
                                  </div>
                                )}
                              </td>
                              {/* Row-branch MUST use FORM-level isWeightBased so all rows align with the table header. */}
                              {form.isWeightBased ? (
                                <>
                                  <td className="px-2 py-2">
                                    <input type="number" step="0.001" value={item.grossWeight}
                                      onChange={e => updateItem(idx, 'grossWeight', e.target.value)}
                                      placeholder="0"
                                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm text-right focus:border-amber-400 outline-none"
                                      data-testid={`project-grn-gross-${idx}`} />
                                  </td>
                                  <td className="px-2 py-2">
                                    <input type="number" step="0.001" value={item.tareWeight}
                                      onChange={e => updateItem(idx, 'tareWeight', e.target.value)}
                                      placeholder="0"
                                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm text-right focus:border-amber-400 outline-none"
                                      data-testid={`project-grn-tare-${idx}`} />
                                  </td>
                                  <td className="px-2 py-2">
                                    <input type="number" step="0.001" value={item.packingWeight}
                                      onChange={e => updateItem(idx, 'packingWeight', e.target.value)}
                                      placeholder="0"
                                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm text-right focus:border-amber-400 outline-none" />
                                  </td>
                                  <td className="px-2 py-2">
                                    <div className={`px-2 py-1.5 rounded text-sm text-right font-bold ${
                                      (parseFloat(item.netWeight) || 0) > (parseFloat(item.poQty) || 0) + 0.001
                                        ? 'bg-red-100 text-red-700 ring-2 ring-red-400'
                                        : 'bg-blue-50 text-blue-700'
                                    }`}
                                      title={item.poQty > 0 ? `Balance PO Qty: ${item.poQty} ${item.uom || ''}` : ''}
                                    >
                                      {item.netWeight?.toLocaleString() || 0}
                                    </div>
                                    {item.poQty > 0 && (
                                      <div className="text-[9px] text-gray-400 text-right mt-0.5">max: {Number(item.poQty).toLocaleString('en-IN')}</div>
                                    )}
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td className="px-2 py-2">
                                    <div
                                      className={`px-2 py-1.5 rounded text-sm text-right font-medium ${
                                        (item.poQty > 0) ? 'bg-gray-100 text-gray-700' : 'bg-amber-50 text-amber-700 border border-amber-200'
                                      }`}
                                      title={item.poQty > 0
                                        ? `Pending in PO: ${item.poQty} ${item.uom || ''}${item.deliveredQty ? ` (already delivered: ${item.deliveredQty})` : ''}`
                                        : 'Fully delivered or PO has 0 qty — click "Diagnose PO" for details'}
                                      data-testid={`project-grn-poqty-${idx}`}
                                    >
                                      {(item.poQty ?? 0).toLocaleString('en-IN')} {item.uom || ''}
                                      {item.poQty === 0 && (
                                        <span className="block text-[9px] font-normal opacity-70">no pending qty</span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-2 py-2">
                                    <input type="number" step="0.01" value={item.invoiceQty}
                                      onChange={e => updateItem(idx, 'invoiceQty', e.target.value)}
                                      placeholder="0"
                                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm text-right focus:border-amber-400 outline-none"
                                      data-testid={`project-grn-invoice-qty-${idx}`} />
                                  </td>
                                  <td className="px-2 py-2">
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={item.receivedQty}
                                      onChange={e => {
                                        const v = e.target.value;
                                        const num = parseFloat(v);
                                        // Hard cap at Balance PO Qty (rejects even 400.1 when balance=400).
                                        if (!Number.isNaN(num) && num > (parseFloat(item.poQty) || 0) + 0.001) {
                                          toast.error(`Received Qty cannot exceed Balance PO Qty (${item.poQty} ${item.uom || ''}) for "${item.commodityName}"`);
                                          updateItem(idx, 'receivedQty', item.poQty); // clamp to balance
                                          return;
                                        }
                                        updateItem(idx, 'receivedQty', v);
                                      }}
                                      onBlur={e => {
                                        const num = parseFloat(e.target.value);
                                        if (!Number.isNaN(num) && num > (parseFloat(item.poQty) || 0) + 0.001) {
                                          updateItem(idx, 'receivedQty', item.poQty);
                                        }
                                      }}
                                      placeholder="0"
                                      min="0"
                                      max={item.poQty || undefined}
                                      disabled={!(item.poQty > 0)}
                                      className={`w-full px-2 py-1.5 border rounded text-sm text-right outline-none ${
                                        (parseFloat(item.receivedQty) || 0) > (parseFloat(item.poQty) || 0) + 0.001
                                          ? 'border-red-500 bg-red-50 focus:border-red-600'
                                          : 'border-gray-300 focus:border-amber-400'
                                      } ${!(item.poQty > 0) ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                      data-testid={`project-grn-received-qty-${idx}`}
                                    />
                                    {item.poQty > 0 && (
                                      <div className="text-[9px] text-gray-400 text-right mt-0.5">max: {Number(item.poQty).toLocaleString('en-IN')}</div>
                                    )}
                                  </td>
                                  <td className="px-2 py-2 text-center">
                                    {item.invoiceQty && item.receivedQty && (
                                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                                        parseFloat(item.receivedQty) >= parseFloat(item.invoiceQty)
                                          ? 'bg-green-100 text-green-700'
                                          : 'bg-orange-100 text-orange-700'
                                      }`}>
                                        {parseFloat(item.receivedQty) >= parseFloat(item.invoiceQty) ? 'Full' : 'Short'}
                                      </span>
                                    )}
                                  </td>
                                </>
                              )}
                              <td className="px-2 py-2">
                                <input type="number" value={item.rate} readOnly
                                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm text-right bg-gray-50 text-gray-700 cursor-not-allowed" />
                              </td>
                              <td className="px-2 py-2">
                                <div className="px-2 py-1.5 bg-green-50 rounded text-sm text-right font-bold text-green-700">
                                  {item.amount?.toLocaleString() || 0}
                                </div>
                              </td>
                              <td className="px-2 py-2 text-center">
                                {form.items.length > 1 && (
                                  <button type="button" onClick={() => removeItemRow(idx)}
                                    className="p-1 text-red-500 hover:bg-red-100 rounded transition" title="Remove item">
                                    <Trash2 size={14} />
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-amber-50 border-t-2 border-amber-200">
                          <tr>
                            <td colSpan={5} className="px-2 py-2 text-right font-bold text-gray-700">GRN Totals:</td>
                            <td className="px-2 py-2">
                              <div className="px-2 py-1.5 bg-blue-100 rounded text-sm text-right font-bold text-blue-800">
                                {(form.isWeightBased ? grnTotals.totalNetWeight : grnTotals.totalReceivedQty).toLocaleString()}
                              </div>
                            </td>
                            <td className="px-2 py-2"></td>
                            <td className="px-2 py-2">
                              <div className="px-2 py-1.5 bg-green-100 rounded text-sm text-right font-bold text-green-800">
                                ₹{grnTotals.totalAmount.toLocaleString()}
                              </div>
                            </td>
                            <td></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Miscellaneous Charges from PO (read-only) */}
              {form.poMiscCharges.length > 0 && (
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <FileText size={16} className="text-amber-500" />
                    Miscellaneous Charges (from PO - Non-GST)
                  </h3>
                  <div className="bg-amber-50 rounded-lg p-3">
                    <div className="space-y-2">
                      {form.poMiscCharges.map((ch, idx) => (
                        <div key={idx} className="flex items-center justify-between px-3 py-2 bg-white rounded-lg border border-amber-100">
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-gray-400 font-mono">{idx + 1}.</span>
                            <span className="text-sm text-gray-700" data-testid={`project-grn-misc-charge-${idx}`}>{ch.description}</span>
                          </div>
                          <span className="text-sm font-semibold text-amber-700">
                            ₹{(parseFloat(ch.amount) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                      ))}
                      <div className="flex items-center justify-between px-3 py-2 bg-amber-100 rounded-lg font-semibold">
                        <span className="text-sm text-amber-800">Total Misc. Charges</span>
                        <span className="text-sm text-amber-800">
                          ₹{form.poMiscCharges.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-2">* Auto-fetched from the linked Project PO and cannot be modified here.</p>
                  </div>
                </div>
              )}

              {/* Vehicle Details */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Vehicle Details</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Vehicle Number</label>
                    <input type="text" value={form.vehicleNo}
                      onChange={e => setForm(f => ({ ...f, vehicleNo: e.target.value }))}
                      placeholder="MH-04-AB-1234"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-amber-400 outline-none"
                      data-testid="project-grn-vehicle-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Driver Name</label>
                    <input type="text" value={form.driverName}
                      onChange={e => setForm(f => ({ ...f, driverName: e.target.value }))}
                      placeholder="Driver name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-amber-400 outline-none"
                      data-testid="project-grn-driver-input" />
                  </div>
                </div>
              </div>

              {/* Result + Remarks */}
              <div className="border-t border-gray-200 pt-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Result <span className="text-red-500">*</span></label>
                    <select
                      value={form.result}
                      onChange={e => setForm(f => ({ ...f, result: e.target.value }))}
                      className={`w-full px-3 py-2 border rounded-lg text-sm outline-none font-medium ${
                        form.result === 'Accepted'
                          ? 'border-green-400 bg-green-50 text-green-700'
                          : 'border-red-400 bg-red-50 text-red-700'
                      }`}
                      data-testid="project-grn-result-select"
                    >
                      <option value="Accepted">Accepted</option>
                      <option value="Shortage">Shortage</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Remarks</label>
                    <input
                      type="text"
                      value={form.remarks}
                      onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))}
                      placeholder="Any additional notes..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-amber-400 outline-none"
                      data-testid="project-grn-remarks-input"
                    />
                  </div>
                </div>
              </div>

              {/* Photos / Documents */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center space-x-2">
                  <Camera size={18} className="text-amber-500" />
                  <span>GRN Photo / Documents ({capturedImages.length})</span>
                </h3>
                {capturedImages.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {capturedImages.map((img, idx) => (
                      <div key={idx} className="relative group aspect-square bg-gray-100 rounded-lg overflow-hidden">
                        {img.startsWith('data:image') ? (
                          <img src={img} alt={`Upload ${idx + 1}`} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-xs text-gray-500 p-2 text-center">
                            <FileText size={28} />
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => setCapturedImages(prev => prev.filter((_, i) => i !== idx))}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition"
                        >
                          x
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {capturedImages.length === 0 && (
                  <div className="bg-gray-100 rounded-xl aspect-video flex flex-col items-center justify-center text-gray-400 mb-3">
                    <Camera size={48} />
                    <p className="mt-2 text-sm">Upload photos / documents</p>
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => fileInputRef.current?.click()}
                    className="flex-1 bg-gray-100 text-gray-700 py-2.5 px-4 rounded-xl font-medium flex items-center justify-center space-x-2 hover:bg-gray-200 transition"
                    data-testid="project-grn-upload-btn">
                    <Upload size={18} /><span>Upload Files</span>
                  </button>
                  <input ref={fileInputRef} type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                    multiple onChange={handleFileUpload} className="hidden" />
                  {capturedImages.length > 0 && (
                    <button type="button" onClick={() => setCapturedImages([])}
                      className="bg-red-50 text-red-600 py-2.5 px-4 rounded-xl font-medium text-sm">
                      Clear All
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 flex justify-end gap-3 sticky bottom-0 bg-white">
              <button type="button" onClick={() => { setShowForm(false); resetForm(); }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition text-sm">
                Cancel
              </button>
              <button type="submit"
                disabled={submitting || !form.poNumber || !form.invoiceNo}
                className="flex items-center gap-2 px-5 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed shadow"
                data-testid="project-grn-submit-btn">
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                {submitting ? 'Saving…' : 'Create GRN'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* DELETE CONFIRM MODAL */}
      {deleteImpact && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="p-5 border-b border-gray-200 bg-rose-600 rounded-t-2xl">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <AlertCircle size={20} /> Delete Project GRN
              </h2>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-gray-700">
                You&apos;re about to delete <strong>{deleteImpact.grn.id}</strong>. This will also remove:
              </p>
              <ul className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm text-rose-900 space-y-1">
                <li>· <strong>{deleteImpact.impact.qaRecords}</strong> linked QA record(s)</li>
                <li>· <strong>{deleteImpact.impact.stockLots}</strong> stock lot(s)</li>
                <li>· <strong>{deleteImpact.impact.payments}</strong> payment row(s)</li>
              </ul>
              <p className="text-xs text-gray-500">This action cannot be undone.</p>
            </div>
            <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl flex justify-end gap-2">
              <button
                onClick={() => { setDeleteImpact(null); setDeletingId(null); }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-5 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700"
                data-testid="project-grn-confirm-delete"
              >
                Delete &amp; Cascade
              </button>
            </div>
          </div>
        </div>
      )}

      {/* GRN Details Modal — mirrors Stores GRN eye-icon view */}
      {viewGRN && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="project-grn-view-modal">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <div className="flex items-center gap-2">
                <Eye className="text-blue-500" size={20} />
                <h2 className="font-bold text-lg">GRN Details - {viewGRN.id}</h2>
                {(viewGRN.items?.length || 1) > 1 && (
                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                    {viewGRN.items.length} Items
                  </span>
                )}
              </div>
              <button onClick={() => setViewGRN(null)} className="p-1 hover:bg-gray-100 rounded">
                <X size={20} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Status & Result */}
              <div className="flex justify-between items-center">
                <div className="flex gap-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    viewGRN.status === 'Verified' ? 'bg-green-100 text-green-700' :
                    viewGRN.status === 'Pending QA' ? 'bg-orange-100 text-orange-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {viewGRN.status}
                  </span>
                  {viewGRN.result && (
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      viewGRN.result === 'Shortage' ? 'bg-rose-100 text-rose-700' :
                      'bg-emerald-100 text-emerald-700'
                    }`}>
                      {viewGRN.result}
                    </span>
                  )}
                </div>
                <span className="text-xs text-gray-500">
                  Date: {viewGRN.date || viewGRN.receivedDate}
                </span>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500 text-xs">PO Number</p>
                  <p className="font-medium">{viewGRN.poNumber || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Invoice Number</p>
                  <p className="font-medium text-blue-600">{viewGRN.invoiceNo || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Eway Bill No</p>
                  <p className="font-medium">{viewGRN.ewayBillNo || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Bill T No</p>
                  <p className="font-medium">{viewGRN.billTNo || '-'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-gray-500 text-xs">Vendor</p>
                  <p className="font-medium">{viewGRN.vendorName || viewGRN.supplier || '-'}</p>
                </div>
                {viewGRN.projectName && (
                  <div className="col-span-2">
                    <p className="text-gray-500 text-xs">Project</p>
                    <p className="font-medium">{viewGRN.projectName}</p>
                  </div>
                )}
              </div>

              {/* Items Table */}
              {viewGRN.items && viewGRN.items.length > 0 && (
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Package size={16} className="text-purple-500" />
                    {(viewGRN.poType === 'service') ? 'Service Items' : 'Commodity Items'} ({viewGRN.items.length})
                  </h3>
                  <div className="bg-gray-50 rounded-lg overflow-hidden border border-gray-200">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">#</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">Commodity</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">{viewGRN.poType === 'service' ? 'SAC' : 'HSN'}</th>
                          <th className="px-3 py-2 text-center font-semibold text-gray-700">UOM</th>
                          <th className="px-3 py-2 text-right font-semibold text-gray-700">Received Qty</th>
                          <th className="px-3 py-2 text-right font-semibold text-gray-700">Rate (₹)</th>
                          <th className="px-3 py-2 text-right font-semibold text-gray-700">Amount (₹)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {viewGRN.items.map((item, idx) => {
                          const qty = item.netWeight || item.receivedQty || item.quantity || 0;
                          return (
                            <tr key={idx} className="bg-white">
                              <td className="px-3 py-2 text-gray-500">{idx + 1}</td>
                              <td className="px-3 py-2 font-medium">{item.commodityName || item.name || item.material}</td>
                              <td className="px-3 py-2 text-gray-600">{item.hsnCode || item.sacCode || '-'}</td>
                              <td className="px-3 py-2 text-center text-gray-600">{item.uom || item.unit || '-'}</td>
                              <td className="px-3 py-2 text-right font-bold text-blue-600">{Number(qty).toLocaleString()}</td>
                              <td className="px-3 py-2 text-right">{item.rate?.toLocaleString() || '-'}</td>
                              <td className="px-3 py-2 text-right font-bold text-green-600">₹{(item.amount || 0).toLocaleString()}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-amber-50 border-t-2 border-amber-200">
                        <tr>
                          <td colSpan={6} className="px-3 py-2 text-right font-bold text-gray-700">Total:</td>
                          <td className="px-3 py-2 text-right font-bold text-green-700">
                            ₹{(viewGRN.totalAmount || viewGRN.amount || 0).toLocaleString('en-IN')}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {/* Photos / Documents */}
              {(() => {
                const attachments = viewGRN.images || viewGRN.documents || viewGRN.photos || [];
                if (!Array.isArray(attachments) || attachments.length === 0) return null;
                return (
                  <div className="border-t border-gray-200 pt-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">
                      Photos / Documents ({attachments.length})
                    </h3>
                    <AttachmentGrid attachments={attachments} />
                  </div>
                );
              })()}

              {/* Remarks */}
              {viewGRN.remarks && (
                <div className="border-t border-gray-200 pt-4">
                  <p className="text-gray-500 text-xs">Remarks</p>
                  <p className="text-sm">{viewGRN.remarks}</p>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setViewGRN(null)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-sm"
                data-testid="project-grn-view-close"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit GRN Modal */}
      {editGRN && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="project-grn-edit-modal">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Edit2 size={18} className="text-amber-600" />
                <h2 className="font-bold text-lg">Edit Project GRN — {editGRN.id}</h2>
              </div>
              <button onClick={() => setEditGRN(null)} className="p-1 hover:bg-gray-100 rounded">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-600 uppercase">Status</label>
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm(p => ({ ...p, status: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  data-testid="edit-grn-status"
                >
                  <option value="Pending QA">Pending QA</option>
                  <option value="Under QA">Under QA</option>
                  <option value="Verified">Verified</option>
                  <option value="Failed">Failed</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 uppercase">Invoice No</label>
                  <input
                    type="text"
                    value={editForm.invoiceNo}
                    onChange={(e) => setEditForm(p => ({ ...p, invoiceNo: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    data-testid="edit-grn-invoice"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 uppercase">Received Date</label>
                  <input
                    type="date"
                    value={editForm.receivedDate}
                    onChange={(e) => setEditForm(p => ({ ...p, receivedDate: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    data-testid="edit-grn-date"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 uppercase">Eway Bill No</label>
                  <input
                    type="text"
                    value={editForm.ewayBillNo}
                    onChange={(e) => setEditForm(p => ({ ...p, ewayBillNo: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    data-testid="edit-grn-eway"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 uppercase">Bill T No</label>
                  <input
                    type="text"
                    value={editForm.billTNo}
                    onChange={(e) => setEditForm(p => ({ ...p, billTNo: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    data-testid="edit-grn-billt"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 uppercase">Remarks</label>
                <textarea
                  rows={3}
                  value={editForm.remarks}
                  onChange={(e) => setEditForm(p => ({ ...p, remarks: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  data-testid="edit-grn-remarks"
                />
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => setEditGRN(null)}
                disabled={savingEdit}
                className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={submitEdit}
                disabled={savingEdit}
                className="px-4 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
                data-testid="edit-grn-save"
              >
                {savingEdit ? <Loader2 size={14} className="animate-spin" /> : <Edit2 size={14} />}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Diagnose PO modal — shows exact per-item breakdown */}
      {diagnosePo && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col">
            <div className="p-5 border-b border-gray-200 flex items-center justify-between bg-amber-50">
              <div>
                <h2 className="text-lg font-bold text-amber-900">🔍 Diagnose PO {diagnosePo}</h2>
                <p className="text-xs text-amber-700">Per-item breakdown — ordered / received / rejected / effective delivered / remaining</p>
              </div>
              <button
                onClick={() => setDiagnosePo(null)}
                className="text-amber-800 hover:bg-amber-100 rounded-full p-2"
                data-testid="diagnose-po-close"
              >✕</button>
            </div>
            <div className="p-5 overflow-y-auto flex-1">
              {diagnoseLoading && <div className="text-gray-500 flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading…</div>}
              {diagnoseData?.error && (
                <div className="bg-rose-50 border border-rose-200 text-rose-800 p-4 rounded-lg text-sm">
                  Failed to fetch diagnose: {diagnoseData.error}
                </div>
              )}
              {diagnoseData?.items && (
                <>
                  <p className="text-sm text-gray-700 mb-3">Vendor: <strong>{diagnoseData.vendor || '-'}</strong></p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">Commodity</th>
                          <th className="px-3 py-2 text-right font-semibold text-gray-700">Ordered</th>
                          <th className="px-3 py-2 text-right font-semibold text-gray-700">Received</th>
                          <th className="px-3 py-2 text-right font-semibold text-gray-700">Rejected</th>
                          <th className="px-3 py-2 text-right font-semibold text-gray-700">Effective Delivered</th>
                          <th className="px-3 py-2 text-right font-semibold text-gray-700">Remaining</th>
                          <th className="px-3 py-2 text-center font-semibold text-gray-700">Shows in GRN?</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {diagnoseData.items.map((it, i) => (
                          <tr key={i} className={it.willAppearInGRN ? 'bg-emerald-50/40' : 'bg-rose-50/40'}>
                            <td className="px-3 py-2">
                              <div className="font-medium">{it.commodityName}</div>
                              <div className="text-[10px] text-gray-500 font-mono">key: {it.normalizedKey}</div>
                            </td>
                            <td className="px-3 py-2 text-right font-semibold">{Number(it.orderedQty || 0).toLocaleString('en-IN')}</td>
                            <td className="px-3 py-2 text-right">{Number(it.receivedTotal || 0).toLocaleString('en-IN')}</td>
                            <td className="px-3 py-2 text-right text-rose-700">{Number(it.rejectedTotal || 0).toLocaleString('en-IN')}</td>
                            <td className="px-3 py-2 text-right">{Number(it.effectiveDelivered || 0).toLocaleString('en-IN')}</td>
                            <td className={`px-3 py-2 text-right font-bold ${it.remaining > 0 ? 'text-emerald-700' : 'text-gray-500'}`}>{Number(it.remaining || 0).toLocaleString('en-IN')}</td>
                            <td className="px-3 py-2 text-center">
                              {it.willAppearInGRN ? (
                                <span className="inline-block px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs font-medium">✓ Yes</span>
                              ) : (
                                <span className="inline-block px-2 py-0.5 bg-rose-100 text-rose-700 rounded text-xs font-medium" title={it.hiddenReason || ''}>
                                  ✕ {it.hiddenReason || 'Hidden'}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* GRN contributions per item */}
                  <div className="mt-4 space-y-3">
                    {diagnoseData.items.filter(it => (it.grnContributions || []).length > 0).map((it, i) => (
                      <div key={i} className="bg-gray-50 rounded-lg p-3">
                        <p className="text-sm font-semibold text-gray-800 mb-1">📋 GRN receipts for <em>{it.commodityName}</em></p>
                        <div className="text-xs text-gray-700 space-y-1">
                          {(it.grnContributions || []).map((g, j) => (
                            <div key={j} className="flex items-center gap-3 flex-wrap">
                              <span className="font-mono text-amber-800">{g.grnId}</span>
                              <span>({g.grnDate || '—'})</span>
                              <span>received: <strong>{g.receivedQty}</strong></span>
                              <span>rejected: <strong className="text-rose-700">{g.rejectedQty}</strong></span>
                              <span className="text-gray-500">QA: {(g.qaStatuses || []).join(', ') || '—'}</span>
                              {g.grnItemName !== it.commodityName && (
                                <span className="text-purple-700">match-name: {g.grnItemName}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setDiagnosePo(null)}
                className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
